import Vue from 'vue'
import { decode, parsePath, withoutBase, withoutTrailingSlash, normalizeURL } from 'ufo'

import { getMatchedComponentsInstances, getChildrenComponentInstancesUsingFetch, promisify, globalHandleError, urlJoin, sanitizeComponent } from './utils'
import NuxtError from '../layouts/error.vue'
import NuxtLoading from './components/nuxt-loading.vue'

import '../styles/tailwind.scss'

import '../styles/_index.scss'

const _98f1ccfa = () => import('../layouts/AdminLayout.vue'  /* webpackChunkName: "layouts/AdminLayout" */).then(m => sanitizeComponent(m.default || m))
const _1a3ae26b = () => import('../layouts/app.vue'  /* webpackChunkName: "layouts/app" */).then(m => sanitizeComponent(m.default || m))
const _4503d2a6 = () => import('../layouts/ClientOnly.vue'  /* webpackChunkName: "layouts/ClientOnly" */).then(m => sanitizeComponent(m.default || m))
const _7edd655b = () => import('../layouts/ConductorLayout.vue'  /* webpackChunkName: "layouts/ConductorLayout" */).then(m => sanitizeComponent(m.default || m))
const _6f6c098b = () => import('../layouts/default.vue'  /* webpackChunkName: "layouts/default" */).then(m => sanitizeComponent(m.default || m))
const _933ae716 = () => import('../layouts/DefaultLayout.vue'  /* webpackChunkName: "layouts/DefaultLayout" */).then(m => sanitizeComponent(m.default || m))
const _6d0c8ca5 = () => import('../layouts/FeedbackLayout.vue'  /* webpackChunkName: "layouts/FeedbackLayout" */).then(m => sanitizeComponent(m.default || m))
const _86db906a = () => import('../layouts/LandingLayout.vue'  /* webpackChunkName: "layouts/LandingLayout" */).then(m => sanitizeComponent(m.default || m))
const _7d998856 = () => import('../layouts/MinimalLayout.vue'  /* webpackChunkName: "layouts/MinimalLayout" */).then(m => sanitizeComponent(m.default || m))
const _7a9d54ce = () => import('../layouts/ModularFrontPageLayout.vue'  /* webpackChunkName: "layouts/ModularFrontPageLayout" */).then(m => sanitizeComponent(m.default || m))
const _5cb37f0c = () => import('../layouts/PaymentLayout.vue'  /* webpackChunkName: "layouts/PaymentLayout" */).then(m => sanitizeComponent(m.default || m))
const _0aac6f0c = () => import('../layouts/PriceCheckerLayout.vue'  /* webpackChunkName: "layouts/PriceCheckerLayout" */).then(m => sanitizeComponent(m.default || m))
const _c56bf910 = () => import('../layouts/PriceOfferLandingLayout.vue'  /* webpackChunkName: "layouts/PriceOfferLandingLayout" */).then(m => sanitizeComponent(m.default || m))
const _347ff3ee = () => import('../layouts/PublicLayout.vue'  /* webpackChunkName: "layouts/PublicLayout" */).then(m => sanitizeComponent(m.default || m))
const _17ced5bf = () => import('../layouts/SalaryCheckerLayout.vue'  /* webpackChunkName: "layouts/SalaryCheckerLayout" */).then(m => sanitizeComponent(m.default || m))
const _cc515246 = () => import('../layouts/WhiteLayout.vue'  /* webpackChunkName: "layouts/WhiteLayout" */).then(m => sanitizeComponent(m.default || m))

let resolvedLayouts = {}
const layouts = { "_AdminLayout": _98f1ccfa,"_app": _1a3ae26b,"_ClientOnly": _4503d2a6,"_ConductorLayout": _7edd655b,"_default": _6f6c098b,"_DefaultLayout": _933ae716,"_FeedbackLayout": _6d0c8ca5,"_LandingLayout": _86db906a,"_MinimalLayout": _7d998856,"_ModularFrontPageLayout": _7a9d54ce,"_PaymentLayout": _5cb37f0c,"_PriceCheckerLayout": _0aac6f0c,"_PriceOfferLandingLayout": _c56bf910,"_PublicLayout": _347ff3ee,"_SalaryCheckerLayout": _17ced5bf,"_WhiteLayout": _cc515246 }

export default {
  render (h, props) {
    const loadingEl = h('NuxtLoading', { ref: 'loading' })

    const layoutEl = h(this.layout || 'nuxt')
    const templateEl = h('div', {
      domProps: {
        id: '__layout'
      },
      key: this.layoutName
    }, [layoutEl])

    const transitionEl = h('transition', {
      props: {
        name: 'layout',
        mode: 'out-in'
      },
      on: {
        beforeEnter (el) {
          // Ensure to trigger scroll event after calling scrollBehavior
          window.$nuxt.$nextTick(() => {
            window.$nuxt.$emit('triggerScroll')
          })
        }
      }
    }, [templateEl])

    return h('div', {
      domProps: {
        id: '__nuxt'
      }
    }, [
      loadingEl,

      transitionEl
    ])
  },

  data: () => ({
    isOnline: true,

    layout: null,
    layoutName: '',

    nbFetching: 0
    }),

  beforeCreate () {
    Vue.util.defineReactive(this, 'nuxt', this.$options.nuxt)
  },
  created () {
    // Add this.$nuxt in child instances
    this.$root.$options.$nuxt = this

    if (process.client) {
      // add to window so we can listen when ready
      window.$nuxt = this

      this.refreshOnlineStatus()
      // Setup the listeners
      window.addEventListener('online', this.refreshOnlineStatus)
      window.addEventListener('offline', this.refreshOnlineStatus)
    }
    // Add $nuxt.error()
    this.error = this.nuxt.error
    // Add $nuxt.context
    this.context = this.$options.context
  },

  async mounted () {
    this.$loading = this.$refs.loading
  },

  watch: {
    'nuxt.err': 'errorChanged'
  },

  computed: {
    isOffline () {
      return !this.isOnline
    },

    isFetching () {
      return this.nbFetching > 0
    },
  },

  methods: {
    refreshOnlineStatus () {
      if (process.client) {
        if (typeof window.navigator.onLine === 'undefined') {
          // If the browser doesn't support connection status reports
          // assume that we are online because most apps' only react
          // when they now that the connection has been interrupted
          this.isOnline = true
        } else {
          this.isOnline = window.navigator.onLine
        }
      }
    },

    async refresh () {
      const pages = getMatchedComponentsInstances(this.$route)

      if (!pages.length) {
        return
      }
      this.$loading.start()

      const promises = pages.map(async (page) => {
        let p = []

        // Old fetch
        if (page.$options.fetch && page.$options.fetch.length) {
          p.push(promisify(page.$options.fetch, this.context))
        }

        if (page.$options.asyncData) {
          p.push(
            promisify(page.$options.asyncData, this.context)
              .then((newData) => {
                for (const key in newData) {
                  Vue.set(page.$data, key, newData[key])
                }
              })
          )
        }

        // Wait for asyncData & old fetch to finish
        await Promise.all(p)
        // Cleanup refs
        p = []

        if (page.$fetch) {
          p.push(page.$fetch())
        }
        // Get all component instance to call $fetch
        for (const component of getChildrenComponentInstancesUsingFetch(page.$vnode.componentInstance)) {
          p.push(component.$fetch())
        }

        return Promise.all(p)
      })
      try {
        await Promise.all(promises)
      } catch (error) {
        this.$loading.fail(error)
        globalHandleError(error)
        this.error(error)
      }
      this.$loading.finish()
    },
    async errorChanged () {
      if (this.nuxt.err) {
        if (this.$loading) {
          if (this.$loading.fail) {
            this.$loading.fail(this.nuxt.err)
          }
          if (this.$loading.finish) {
            this.$loading.finish()
          }
        }

        let errorLayout = (NuxtError.options || NuxtError).layout;

        if (typeof errorLayout === 'function') {
          errorLayout = errorLayout(this.context)
        }

        await this.loadLayout(errorLayout)

        this.nuxt.errPageReady = true
        this.setLayout(errorLayout)
      }
    },

    setLayout (layout) {
      if (!layout || !resolvedLayouts['_' + layout]) {
        layout = 'default'
      }
      this.layoutName = layout
      let _layout = '_' + layout
      this.layout = resolvedLayouts[_layout]
      return this.layout
    },
    loadLayout (layout) {
      const undef = !layout
      const nonexistent = !(layouts['_' + layout] || resolvedLayouts['_' + layout])
      let _layout = '_' + ((undef || nonexistent) ? 'default' : layout)
      if (resolvedLayouts[_layout]) {
        return Promise.resolve(resolvedLayouts[_layout])
      }
      return layouts[_layout]()
        .then((Component) => {
          resolvedLayouts[_layout] = Component
          delete layouts[_layout]
          return resolvedLayouts[_layout]
        })
        .catch((e) => {
          if (this.$nuxt) {
            return this.$nuxt.error({ statusCode: 500, message: e.message })
          }
        })
    },
  },

  components: {
    NuxtLoading
  }
}
